import { useState, useEffect } from "react";
import { Group, Text, ActionIcon, Avatar, Box, rem, Menu, Tooltip, UnstyledButton, Popover, Indicator, Burger, Drawer, Stack, Divider } from "@mantine/core";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import {
  IconBell,
  IconHelp,
  IconSettings,
  IconLogout,
  IconUser,
  IconUsers,
  IconCheck,
  IconSwitchHorizontal,
  IconExternalLink,
  IconChevronDown,
  IconLayoutGrid,
  IconBuilding,
  IconSparkles,
  IconEye,
} from "@tabler/icons-react";
import { Link, useNavigate, useTenantPathname } from "@/routing/accountRouting";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useAssistant } from "@/context/AssistantContext";
import { useAssistantName } from "@/components/assistant/assistantBrand";
import { useBranding } from "@/context/BrandingContext";
import { useWorkspace } from "@/context/WorkspaceContext";
import { useMenuVisibility } from "@/hooks/useMenuVisibility";
import { useSectionAccess } from "@/hooks/useSectionAccess";
import { setViewAsCustomer, useViewAsCustomer } from "@/hooks/useViewAsCustomer";
import { HiddenFromCustomerMark } from "@/components/auth/HiddenFromCustomer";
import { useNotify } from "@/hooks/useNotify";
import NotificationPanel from "@/components/notifications/NotificationPanel";
import classes from "./AppHeader.module.css";

const NAV_ITEMS = [
  // Reports live at /reports (the URLs predate the menu entry) but are gated
  // and navigated as an Insights submenu — they light up Insights too.
  { key: "insights", label: "Insights", to: "/insights", match: (p: string) => p.startsWith("/insights") || p.startsWith("/reports") },
  { key: "activation", label: "Activation", to: "/activation/segments", match: (p: string) => p.startsWith("/activation") },
  { key: "data-management", label: "Data Management", to: "/data-management", match: (p: string) => p.startsWith("/data-management") },
  { key: "automations", label: "Automations", to: "/automations", match: (p: string) => p.startsWith("/automations") },
];

export function AppHeader() {
  const { user, activeAccount, allAccounts, switchAccount, logout } = useAuth();
  const assistant = useAssistant();
  const { branding } = useBranding();
  const assistantName = useAssistantName();
  const { workspaces, activeWorkspace, switchWorkspace } = useWorkspace();
  const { isMenuVisible } = useMenuVisibility();
  const { unreadCount } = useNotify();
  const [panelOpen, setPanelOpen] = useState(false);

  const [drawerOpened, { open: openDrawer, close: closeDrawer }] = useDisclosure(false);
  const isMobile = useMediaQuery("(max-width: 768px)");
  const isTablet = useMediaQuery("(max-width: 1024px)");
  const isCompact = useMediaQuery("(max-width: 1280px)");

  const sectionAccess = useSectionAccess();
  const viewAsCustomer = useViewAsCustomer();
  const visibleNavItems = NAV_ITEMS.map((item) => ({ ...item, access: sectionAccess.menu(item.key) })).filter(
    (item) => item.access.shown
  );
  // Raw pathname (with the `/a/<account>` prefix) for the settings-return-path
  // bookmark below, which is handed straight back to navigate() later; the
  // tenant pathname (prefix stripped) is what every route comparison uses.
  const { pathname } = useLocation();
  const tenantPathname = useTenantPathname();
  const navigate = useNavigate();

  // Close notification panel and drawer on route change
  useEffect(() => {
    setPanelOpen(false);
    closeDrawer();
  }, [pathname]);

  const handleSettingsClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!tenantPathname.startsWith("/settings")) {
      sessionStorage.setItem("settings-return-path", pathname);
    }
    navigate("/settings/profile");
  };

  const handleSignOut = async () => {
    try {
      await logout();
      window.location.href = "/login";
    } catch (error) {
      console.error("Sign out error:", error);
    }
  };

  const userName = user?.firstName || "User";
  const userInitials = userName.charAt(0).toUpperCase();

  // Reports are workspace-scoped like the rest of Insights, so /reports gets
  // the switcher as well.
  const showWorkspaceSwitcher =
    tenantPathname.startsWith("/insights") ||
    tenantPathname.startsWith("/reports") ||
    tenantPathname.startsWith("/automations");

  return (
    <>
      <Box
        h="100%"
        px="md"
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "auto 1fr" : "auto 1fr auto",
          alignItems: "center",
        }}
      >
        {/* LEFT: Burger (mobile) + Logo + Workspace switcher */}
        <Group gap="md" wrap="nowrap" style={{ overflow: "hidden", minWidth: 0 }}>
          {isMobile && !tenantPathname.startsWith("/settings") && (
            <Burger opened={drawerOpened} onClick={drawerOpened ? closeDrawer : openDrawer} size="sm" />
          )}

          <Link to={isMenuVisible("dashboard") ? "/dashboard" : "/"} style={{ textDecoration: "none", flexShrink: 0 }}>
            <Group gap="xs" wrap="nowrap" style={{ cursor: "pointer" }}>
              <img
                src={branding.logoNoTextUrl ?? branding.logoUrl}
                alt={`${branding.productName} Logo`}
                style={{ width: rem(32), height: rem(32) }}
              />
              {!isCompact && (
                <span style={{
                  fontFamily: "Google Sans Flex, Google Sans, sans-serif",
                  fontSize: rem(20),
                  fontWeight: 500,
                  letterSpacing: "0.04em",
                  color: "#888",
                  whiteSpace: "nowrap",
                }}>
                  {branding.shortName ?? branding.productName}
                </span>
              )}
            </Group>
          </Link>

          {showWorkspaceSwitcher && !isMobile && (
            <Menu shadow="md" width={240} position="bottom-start">
              <Menu.Target>
                <UnstyledButton
                  style={{
                    padding: isCompact ? "6px" : "6px 12px 6px 10px",
                    borderRadius: rem(8),
                    border: isCompact ? "none" : "1px solid var(--mantine-color-gray-3)",
                    transition: "background-color 0.2s",
                    flexShrink: 0,
                  }}
                  styles={{
                    root: {
                      "&:hover": {
                        backgroundColor: "var(--mantine-color-gray-0)",
                      },
                    },
                  }}
                >
                  <Group gap={6} wrap="nowrap">
                    <IconLayoutGrid size={16} color="var(--mantine-color-gray-6)" />
                    {!isCompact && (
                      <Text size="sm" fw={500} c="dark" style={{ maxWidth: 160 }} truncate>
                        {activeWorkspace?.name || "Select workspace"}
                      </Text>
                    )}
                    <IconChevronDown size={14} color="var(--mantine-color-gray-5)" />
                  </Group>
                </UnstyledButton>
              </Menu.Target>

              <Menu.Dropdown>
                <Menu.Label>Workspace</Menu.Label>
                {workspaces.map((ws) => {
                  const isActive = ws.id === activeWorkspace?.id;
                  const wsType = ws.type?.toUpperCase();
                  // Company-wide = building; personal = single user; shared = group.
                  const wsIcon =
                    wsType === "COMPANY" ? (
                      <IconBuilding size={14} />
                    ) : wsType === "PERSONAL" ? (
                      <IconUser size={14} />
                    ) : (
                      <IconUsers size={14} />
                    );
                  return (
                    <Menu.Item
                      key={ws.id}
                      leftSection={wsIcon}
                      rightSection={isActive ? <IconCheck size={14} /> : null}
                      onClick={() => switchWorkspace(ws.id)}
                      style={{
                        backgroundColor: isActive
                          ? "var(--mantine-color-blue-0)"
                          : undefined,
                        fontWeight: isActive ? 500 : 400,
                      }}
                    >
                      {ws.name}
                    </Menu.Item>
                  );
                })}
                <Menu.Divider />
                <Menu.Item
                  leftSection={<IconSettings size={14} />}
                  onClick={() => {
                    if (!tenantPathname.startsWith("/settings")) {
                      sessionStorage.setItem("settings-return-path", pathname);
                    }
                    navigate("/settings/workspaces");
                  }}
                >
                  Manage Workspaces
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          )}
        </Group>

        {/* CENTER: Main navigation (hidden on settings pages and mobile) */}
        {!isMobile && (
          tenantPathname.startsWith("/settings") ? (
            <div />
          ) : (
            <nav className={classes.navGroup}>
              {visibleNavItems.map((item) => {
                const active = item.match(tenantPathname);
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={`${classes.navItem} ${active ? classes.navItemActive : ""}`}
                  >
                    {item.label}
                    {item.access.hiddenFromCustomer && (
                      <>
                        {" "}
                        <HiddenFromCustomerMark size={13} />
                      </>
                    )}
                  </Link>
                );
              })}
            </nav>
          )
        )}

        {/* RIGHT: Assistant + Help + Notifications + User/Account Menu */}
        <Group gap="md" justify="flex-end">
          {assistant.enabled && (
            <Tooltip
              label={assistantName ? `Ask ${assistantName}` : "Ask the assistant"}
              position="bottom"
              withArrow
            >
              <ActionIcon
                variant="subtle"
                color="gray"
                aria-label={assistantName ? `Ask ${assistantName}` : "Ask the assistant"}
                onClick={assistant.toggle}
              >
                <IconSparkles size={20} />
              </ActionIcon>
            </Tooltip>
          )}

          <Tooltip label="Help & Docs" position="bottom" withArrow>
            <ActionIcon
              component={Link}
              to="/docs"
              variant="subtle"
              color="gray"
              aria-label="Help and documentation"
            >
              <IconHelp size={20} />
            </ActionIcon>
          </Tooltip>

          <Popover
            width={380}
            position="bottom-end"
            shadow="lg"
            opened={panelOpen}
            onChange={setPanelOpen}
          >
            <Popover.Target>
              <Indicator
                label={unreadCount}
                size={16}
                disabled={unreadCount === 0}
                color="red"
              >
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  aria-label="Notifications"
                  onClick={() => setPanelOpen((o) => !o)}
                >
                  <IconBell size={20} />
                </ActionIcon>
              </Indicator>
            </Popover.Target>
            <Popover.Dropdown p={0}>
              <NotificationPanel onClose={() => setPanelOpen(false)} />
            </Popover.Dropdown>
          </Popover>

          <Menu shadow="md" width={260} position="bottom-end">
            <Menu.Target>
              <UnstyledButton
                style={{
                  padding: "4px 8px 4px 4px",
                  borderRadius: rem(8),
                  transition: "background-color 0.2s",
                }}
                styles={{
                  root: {
                    "&:hover": {
                      backgroundColor: "var(--mantine-color-gray-1)",
                    },
                  },
                }}
              >
                <Group gap="sm">
                  <Avatar radius="xl" color="blue" size={isTablet ? 32 : 36}>
                    {userInitials}
                  </Avatar>
                  {!isTablet && (
                    <Box style={{ lineHeight: 1.2 }}>
                      <Text size="sm" fw={500} c="dark">
                        {userName}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {activeAccount?.name || "No account"}
                      </Text>
                    </Box>
                  )}
                  <IconChevronDown size={14} color="var(--mantine-color-gray-5)" />
                </Group>
              </UnstyledButton>
            </Menu.Target>

            <Menu.Dropdown>
              <Menu.Item
                leftSection={<IconUser size={14} />}
                onClick={handleSettingsClick}
              >
                Profile
              </Menu.Item>
              <Menu.Item
                leftSection={<IconSettings size={14} />}
                onClick={handleSettingsClick}
              >
                Settings
              </Menu.Item>

              {allAccounts && allAccounts.length > 1 && (
                <>
                  <Menu.Divider />
                  <Menu.Label>
                    <Group gap={4}>
                      <IconSwitchHorizontal size={12} />
                      Switch Account
                    </Group>
                  </Menu.Label>
                  {allAccounts.map((acc) => {
                    const isActive = acc.id === activeAccount?.id;
                    return (
                      <Menu.Item
                        key={acc.id}
                        rightSection={isActive ? <IconCheck size={14} /> : null}
                        onClick={() => switchAccount(acc.id)}
                        style={{
                          backgroundColor: isActive
                            ? "var(--mantine-color-blue-0)"
                            : undefined,
                          fontWeight: isActive ? 500 : 400,
                        }}
                      >
                        {acc.name}
                      </Menu.Item>
                    );
                  })}
                </>
              )}

              {/* White-label: accounts the user holds on OTHER partner domains.
                  Those aren't switchable from here (per-domain isolation) — link
                  the user to sign in there instead. */}
              {user?.otherPartnerAccounts && user.otherPartnerAccounts.length > 0 && (
                <>
                  <Menu.Divider />
                  <Menu.Label>
                    <Group gap={4}>
                      <IconExternalLink size={12} />
                      Your other sites
                    </Group>
                  </Menu.Label>
                  {user.otherPartnerAccounts.map((link) => (
                    <Menu.Item
                      key={link.url}
                      component="a"
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      leftSection={<IconExternalLink size={14} />}
                    >
                      {link.label}
                    </Menu.Item>
                  ))}
                </>
              )}

              {user?.isSuperadmin && (
                <>
                  <Menu.Divider />
                  {/* Superadmin only: show this account the way its members
                      see it, without the sections hidden from them. */}
                  <Menu.Item
                    leftSection={<IconEye size={14} />}
                    rightSection={viewAsCustomer ? <IconCheck size={14} /> : undefined}
                    onClick={() => setViewAsCustomer(!viewAsCustomer)}
                  >
                    View as customer
                  </Menu.Item>
                </>
              )}

              <Menu.Divider />
              <Menu.Item
                leftSection={<IconLogout size={14} />}
                color="red"
                onClick={handleSignOut}
              >
                Sign out
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Box>

      {/* Mobile navigation drawer */}
      <Drawer
        opened={drawerOpened}
        onClose={closeDrawer}
        size="xs"
        padding="md"
        title="Navigation"
        zIndex={1000}
      >
        <Stack gap={4}>
          {visibleNavItems.map((item) => {
            const active = item.match(tenantPathname);
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`${classes.drawerNavItem} ${active ? classes.drawerNavItemActive : ""}`}
                onClick={closeDrawer}
              >
                {item.label}
                {item.access.hiddenFromCustomer && (
                  <>
                    {" "}
                    <HiddenFromCustomerMark size={13} />
                  </>
                )}
              </Link>
            );
          })}
        </Stack>

        {showWorkspaceSwitcher && (
          <>
            <Divider my="md" />
            <Text size="xs" fw={500} c="dimmed" mb="xs" px={4}>Workspace</Text>
            <Stack gap={4}>
              {workspaces.map((ws) => {
                const isActive = ws.id === activeWorkspace?.id;
                return (
                  <UnstyledButton
                    key={ws.id}
                    onClick={() => { switchWorkspace(ws.id); closeDrawer(); }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "8px 12px",
                      borderRadius: 8,
                      backgroundColor: isActive ? "var(--mantine-color-blue-0)" : undefined,
                      fontWeight: isActive ? 500 : 400,
                    }}
                  >
                    <IconLayoutGrid size={16} color="var(--mantine-color-gray-6)" />
                    <Text size="sm">{ws.name}</Text>
                    {isActive && <IconCheck size={14} color="var(--mantine-color-blue-6)" />}
                  </UnstyledButton>
                );
              })}
            </Stack>
          </>
        )}
      </Drawer>
    </>
  );
}
